import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { AdminApplication } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';

interface ApplicationCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (application: AdminApplication) => void;
}

/**
 * `POST /v1/admin/applications` (A3 contract). `notifySecret` is write-only:
 * accepted here, never returned by this or any other operation (the created
 * `AdminApplication` shape has no such property at all). A `409 conflict` on
 * a duplicate code is rendered as a specific, formal-register error on the
 * code field, mirroring `TenantCreateDialog`.
 */
export function ApplicationCreateDialog({ open, onClose, onCreated }: ApplicationCreateDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notifyUrl, setNotifyUrl] = useState('');
  const [notifySecret, setNotifySecret] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setCode('');
    setName('');
    setNotifyUrl('');
    setNotifySecret('');
    setCodeError(undefined);
    setAccessDenied(false);
    onClose();
  }

  async function handleSubmit() {
    setCodeError(undefined);
    setSubmitting(true);
    try {
      const application = await payApiClient.createAdminApplication({
        code: code.trim(),
        name: name.trim(),
        notifyUrl: notifyUrl.trim() || undefined,
        notifySecret: notifySecret.trim() || undefined,
      });
      notify(translate('pay.applications.create.success'), { type: 'success' });
      setCode('');
      setName('');
      setNotifyUrl('');
      setNotifySecret('');
      onCreated(application);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'conflict') {
        setCodeError(translate('pay.applications.create.conflictError'));
      } else if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = code.trim().length > 0 && name.trim().length > 0 && !submitting;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.applications.create.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={translate('pay.applications.create.fieldCode')}
              helperText={codeError ?? translate('pay.applications.create.fieldCodeHelp')}
              error={Boolean(codeError)}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setCodeError(undefined);
              }}
              fullWidth
            />
            <TextField
              size="small"
              label={translate('pay.applications.create.fieldName')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label={translate('pay.applications.create.fieldNotifyUrl')}
              value={notifyUrl}
              onChange={(event) => setNotifyUrl(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              autoComplete="off"
              label={translate('pay.applications.create.fieldNotifySecret')}
              helperText={translate('pay.applications.create.fieldNotifySecretHelp')}
              value={notifySecret}
              onChange={(event) => setNotifySecret(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.applications.create.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {translate('pay.applications.create.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
